import React from "react";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Activity, Globe, Monitor, Smartphone, UserCog, Users } from "lucide-react";

export default function Header({ view, setView, kiosk, setKiosk }) {
  const { lang, setLang, t } = useLang();
  const langs = [
    { code: "en", label: "English" },
    { code: "hi", label: "हिंदी" },
    { code: "ta", label: "தமிழ்" },
  ];
  const current = langs.find((l) => l.code === lang);

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/85 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-600 text-white shadow-sm">
            <Activity className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-[Outfit] text-lg font-bold leading-tight tracking-tight text-slate-900 sm:text-xl">
              {t("app_title")}
            </h1>
            <p className="hidden text-xs text-slate-500 sm:block">{t("app_subtitle")}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Kiosk / Mobile toggle */}
          <div className="hidden rounded-full border border-slate-200 bg-slate-50 p-1 sm:flex" data-testid="view-mode-toggle">
            <button
              onClick={() => setKiosk(true)}
              data-testid="kiosk-mode-toggle"
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                kiosk ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <Monitor className="h-3.5 w-3.5" /> {t("kiosk_mode")}
            </button>
            <button
              onClick={() => setKiosk(false)}
              data-testid="mobile-view-toggle"
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                !kiosk ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <Smartphone className="h-3.5 w-3.5" /> {t("mobile_view")}
            </button>
          </div>

          {/* Language */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="language-switcher" className="rounded-full">
                <Globe className="mr-1.5 h-3.5 w-3.5" />
                {current?.label}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white">
              {langs.map((l) => (
                <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} data-testid={`lang-${l.code}`}>
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* View toggle */}
          <Button
            onClick={() => setView(view === "patient" ? "doctor" : "patient")}
            data-testid="portal-toggle"
            size="sm"
            className={`rounded-full ${view === "doctor" ? "bg-teal-600 hover:bg-teal-700" : "bg-sky-600 hover:bg-sky-700"} text-white`}
          >
            {view === "patient" ? (
              <><UserCog className="mr-1.5 h-3.5 w-3.5" /> {t("doctor_portal")}</>
            ) : (
              <><Users className="mr-1.5 h-3.5 w-3.5" /> {t("patient_mode")}</>
            )}
          </Button>
        </div>
      </div>
    </header>
  );
}
